import React, { useState } from 'react'
import { Badge, Button, Dropdown, Radio, Space, theme } from 'antd'
import { FilterOutlined } from '@ant-design/icons'

/**
 * A dropdown showing active chart-driven filters, allowing the user to switch
 * or clear each one.
 *
 * @param {Array} filters - Array of filter descriptors:
 *   {
 *     key:      string          — unique key
 *     label:    string          — display name for the category (e.g. 'Plant')
 *     value:    any             — current filter value (falsey = not active)
 *     options:  [{label, value}]— selectable options derived from chart data
 *     onChange: (value) => void — called with new value, or null to clear
 *   }
 */
const ActiveFiltersDropdown = ({ filters = [] }) => {
  const { token } = theme.useToken()
  const [open, setOpen] = useState(false)

  const activeFilters = filters.filter((f) => f.value)
  if (activeFilters.length === 0) return null

  const panelStyle = {
    background: token.colorBgElevated,
    borderRadius: token.borderRadiusLG,
    boxShadow: token.boxShadowSecondary,
    padding: '8px 0',
    minWidth: 220,
  }

  const categoryLabelStyle = {
    display: 'block',
    padding: '4px 16px 2px',
    fontSize: token.fontSizeSM,
    color: token.colorTextSecondary,
    fontWeight: token.fontWeightStrong,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  }

  const radioGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 16px 8px',
    gap: 4,
  }

  const dividerStyle = {
    margin: '4px 0',
    borderTop: `1px solid ${token.colorBorderSecondary}`,
  }

  const dropdownContent = (
    <div style={panelStyle}>
      {activeFilters.map((filter, idx) => (
        <React.Fragment key={filter.key}>
          {idx > 0 && <div style={dividerStyle} />}
          <span style={categoryLabelStyle}>{filter.label}</span>
          <Radio.Group
            style={radioGroupStyle}
            value={filter.value}
            onChange={(e) => filter.onChange(e.target.value || null)}
          >
            <Space direction="vertical" size={2}>
              <Radio value={null}>None</Radio>
              {filter.options.map((opt) => (
                <Radio key={opt.value} value={opt.value}>
                  {opt.label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </React.Fragment>
      ))}
    </div>
  )

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      placement="bottomRight"
    >
      <Badge count={activeFilters.length} size="small">
        <Button
          size="small"
          icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
          type="text"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            color: token.colorTextSecondary,
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
          }}
        >
          Filters
        </Button>
      </Badge>
    </Dropdown>
  )
}

export default ActiveFiltersDropdown
